/**
 * Auth Context - Gerenciamento de estado de autenticação
 */
import { createContext, useState, useEffect, useCallback, useContext } from 'react'
import AuthService from '@services/auth'
import { APP_CONFIG } from '@config/constants'
import { isClientPortalSurface } from '@config/appSurface'

export const AuthContext = createContext(null)

function isAdminUser(user) {
  if (!user) return false

  const normalizedRole = String(user.role || '').toUpperCase()
  const roleId = Number(user.roleId)
  const roleIds = Array.isArray(user.roleIds) ? user.roleIds.map(Number) : []

  return normalizedRole === 'ADMIN'
    || (Number.isFinite(roleId) && APP_CONFIG.ADMIN_ROLE_IDS.includes(roleId))
    || roleIds.some((id) => Number.isFinite(id) && APP_CONFIG.ADMIN_ROLE_IDS.includes(id))
}

function isSuperAdminUser(user) {
  if (!user) return false

  const normalizedRole = String(user.role || '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_')
  const roleId = Number(user.roleId)
  const roleIds = Array.isArray(user.roleIds) ? user.roleIds.map(Number) : []

  return normalizedRole === 'SUPER_ADMIN'
    || (Number.isFinite(roleId) && APP_CONFIG.SUPER_ADMIN_ROLE_IDS.includes(roleId))
    || roleIds.some((id) => Number.isFinite(id) && APP_CONFIG.SUPER_ADMIN_ROLE_IDS.includes(id))
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [sessionVersion, setSessionVersion] = useState(0)

  const applySession = useCallback((result) => {
    if (!result.success || !result.user) return false
    setUser(result.user)
    setIsAuthenticated(true)
    setSessionVersion((version) => version + 1)
    return true
  }, [])

  const refreshSession = useCallback(async () => {
    const refreshResult = await AuthService.refreshSession()
    if (applySession(refreshResult)) return true

    setUser(null)
    setIsAuthenticated(false)
    return false
  }, [applySession])

  useEffect(() => {
    const init = async () => {
      try {
        const initResult = await AuthService.initAuth()
        if (applySession(initResult)) {
          if (AuthService.shouldRefreshSoon()) await refreshSession()
          return
        }
        await refreshSession()
      } catch (error) {
        console.error('Erro ao inicializar auth:', error)
      } finally {
        setLoading(false)
      }
    }
    init()
  }, [applySession, refreshSession])

  useEffect(() => {
    if (!isAuthenticated) return undefined

    const timeoutId = window.setTimeout(refreshSession, AuthService.getRefreshDelayMs())

    const refreshOnFocus = () => {
      if (document.visibilityState === 'visible' && AuthService.shouldRefreshSoon()) refreshSession()
    }

    const expireSession = () => {
      AuthService.logout()
      setUser(null)
      setIsAuthenticated(false)
    }

    window.addEventListener('focus', refreshOnFocus)
    document.addEventListener('visibilitychange', refreshOnFocus)
    window.addEventListener('auth:session-expired', expireSession)

    return () => {
      window.clearTimeout(timeoutId)
      window.removeEventListener('focus', refreshOnFocus)
      document.removeEventListener('visibilitychange', refreshOnFocus)
      window.removeEventListener('auth:session-expired', expireSession)
    }
  }, [isAuthenticated, sessionVersion, refreshSession])

  const login = useCallback(async (email, password, options = null) => {
    const resolvedOptions = options ?? { allowLegacyFallback: !isClientPortalSurface() }
    const result = await AuthService.login(email, password, resolvedOptions)
    if (result.success) applySession(result)
    return result
  }, [applySession])

  const changePassword = useCallback(async ({ currentPassword, newPassword, confirmPassword }) => {
    if (!user?.id) return { success: false, error: 'Usuário não autenticado.' }

    const result = await AuthService.changePassword({
      userId: user.id,
      currentPassword,
      newPassword,
      confirmPassword,
    })

    if (result.success && result.user) setUser(result.user)
    return result
  }, [user])

  const logout = useCallback(() => {
    AuthService.logout()
    setUser(null)
    setIsAuthenticated(false)
  }, [])

  const isAdmin = isAdminUser(user)
  const isSuperAdmin = isSuperAdminUser(user)
  const normalizedRole = String(user?.role || '').trim().toUpperCase()
  const isExternalClient = normalizedRole === 'CLIENT'
  const hasPermission = (permission) => permission === 'users.create'
    ? isAdmin || isSuperAdmin
    : isAdmin || isSuperAdmin || (user?.permissions || []).includes(permission)

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    changePassword,
    isAdmin,
    isSuperAdmin,
    isExternalClient,
    isAdvogado: normalizedRole === 'LAWYER' || normalizedRole === 'ADVOGADO',
    hasPermission,
    canAccessPortal: isExternalClient || hasPermission('portal.view'),
    canAccessUsers: hasPermission('users.view'),
    canListUsers: hasPermission('users.list'),
    canCreateUsers: hasPermission('users.create'),
    canEditUsers: hasPermission('users.edit'),
    canAccessDashboard: hasPermission('dashboard.view'),
    canAccessReports: hasPermission('reports.view'),
    canAccessProcessReports: hasPermission('reports.processes.view'),
    canAccessClickBusReports: hasPermission('reports.clickbus.view'),
    canAccessBoard: hasPermission('board.view'),
    canAccessSimulator: hasPermission('simulator.view'),
    canManageSimulatorSettings: hasPermission('simulator.settings.manage'),
    canManageLegalTemplates: hasPermission('templates.manage'),
    canAccessForms: hasPermission('forms.view'),
    canAccessRules: isAdmin,
    canAccessSystemSettings: isSuperAdmin,
    canManageBoard: hasPermission('board.view'),
    canViewAgenda: hasPermission('board.view'),
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within an AuthProvider')
  return context
}
